import { type ControllerRenderProps, useController } from 'react-hook-form';
import isemail from 'isemail';

import { useIntl } from '@doxyme/locale';
import type {
  InviteViaEmailFormReturn,
  InviteViaEmailFormValues,
} from '../inviteViaEmailForm.types';

export const emailFieldMaxLength = 25;

/**
    Parses the string into an array based on multiple separators. 
    [] → A character class — it matches any one character from the set of characters inside the brackets.
    \s → Matches any whitespace character (spaces, tabs, newlines).
    , - Matches a comma.
    ; - Matches a semicolon.
    /u → The Unicode flag (u) allows the regex to correctly handle Unicode characters.
   */
export const parseCsvToArray = (value: string) => {
  const email = value
    .split(/[\s,;]/u)
    .map((email) => email.trim())
    .filter(Boolean);

  return [...new Set(email)]; // return only unique addresses
};

export const useEmailField = (
  form: InviteViaEmailFormReturn,
): ControllerRenderProps<InviteViaEmailFormValues, 'email'> => {
  const { formatMessage } = useIntl();

  const getInvalidEmails = (input: string) => {
    const emails = parseCsvToArray(input);
    return emails.filter((email) => !isemail.validate(email));
  };

  return useController({
    name: 'email',
    defaultValue: '',
    control: form.control,
    rules: {
      required: {
        value: true,
        message: formatMessage(
          { id: 'form.validation.required' },
          {
            friendlyName: formatMessage({
              id: 'inviteViaEmailForm.email.friendlyName',
            }),
          },
        ),
      },
      validate: {
        isValidEmail: (value) => {
          const invalidEmails = getInvalidEmails(value);

          if (invalidEmails.length > 0) {
            return formatMessage(
              { id: 'inviteViaEmailForm.email.validation' },
              {
                itemCount: invalidEmails.length,
                invalidEmails: invalidEmails.join(','),
              },
            );
          }
          return true;
        },
        maxLength: (value) => {
          const emails = parseCsvToArray(value);

          if (emails.length > emailFieldMaxLength) {
            return formatMessage(
              { id: 'inviteViaEmailForm.email.maxLength' },
              { maxLength: emailFieldMaxLength },
            );
          }

          return true;
        },
      },
    },
  }).field;
};
